import { area, intersect, polygon } from '@turf/turf';
import { FieldGeometry } from 'base/types/RecommendationCropwise';
import { SoilType } from 'base/types/SoilType';
import { SoilColors } from 'utils/constants/SoilColors';
import { colors } from 'syngenta-digital-cropwise-react-ui-kit';

export interface SoilBreakdownItem {
  key: string;
  label: string;
  category: string;
  color: string;
  hectares: number;
  percentage: number;
}

function soilIdentity(
  cell: SoilType
): Pick<SoilBreakdownItem, 'key' | 'label' | 'category' | 'color'> {
  const category = cell.texture.trim().toUpperCase();
  const label = cell.texture_raw.trim().toUpperCase();
  const key = `${category} ${label}`;
  const soil = SoilColors.find((entry) => entry.key === key || entry.label === label);
  return {
    key,
    label: label.toLowerCase().replace(/(^|\s)\S/g, (letter) => letter.toUpperCase()),
    category:
      category === 'MED'
        ? 'Medium'
        : category.toLowerCase().replace(/^\S/, (letter) => letter.toUpperCase()),
    color: soil?.color ?? colors.neutral30,
  };
}

function clippedCellArea(cell: SoilType, resolution: number, geometry: FieldGeometry): number {
  if (!Number.isFinite(cell.longitude) || !Number.isFinite(cell.latitude)) {
    throw new Error('Soil grid coordinates are unavailable');
  }
  const x = cell.longitude;
  const y = cell.latitude;
  const half = resolution / 2;
  const square = polygon([
    [
      [x - half, y - half],
      [x + half, y - half],
      [x + half, y + half],
      [x - half, y + half],
      [x - half, y - half],
    ],
  ]);
  const clipped = intersect(polygon(geometry.coordinates), square);
  return clipped ? area(clipped) / 10000 : 0;
}

export function getSoilBreakdown(
  cells: SoilType[],
  resolution: number,
  geometry: FieldGeometry
): SoilBreakdownItem[] {
  if (!cells.length) {
    return [];
  }
  if (!Number.isFinite(resolution) || resolution <= 0 || geometry.type !== 'Polygon') {
    throw new Error('Soil grid resolution or field geometry is unavailable');
  }
  const grouped = new Map<string, SoilBreakdownItem>();
  cells.forEach((cell) => {
    const hectares = clippedCellArea(cell, resolution, geometry);
    if (hectares <= 0) {
      return;
    }
    const identity = soilIdentity(cell);
    const entry = grouped.get(identity.key);
    if (entry) {
      entry.hectares += hectares;
    } else {
      grouped.set(identity.key, { ...identity, hectares, percentage: 0 });
    }
  });
  const total = Array.from(grouped.values()).reduce((sum, entry) => sum + entry.hectares, 0);
  return Array.from(grouped.values())
    .map((entry) => ({ ...entry, percentage: (entry.hectares / total) * 100 }))
    .sort((left, right) => right.hectares - left.hectares);
}
